import { Dispatch, DragEvent, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Chip, Stack, Typography } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  FieldLabel,
  QueryAdderFieldGridItem,
  QueryAdderInheritedParentServiceSection,
  QueryAdderLayoutSkeleton,
  QueryAdderModalShell,
  QueryAdderScopedStyles,
  QueryAdderStickyHeader,
  QueryAdderTopicLinkingCard,
  QueryAdderUploadDropzone,
  SectionCard,
  StatusPill,
  queryAdderOneColumnFieldGridSx,
  queryAdderTwoColumnGridSx,
  renderQueryAdderCell,
} from './QueryAdderLayoutShared';
import VerificationModal from '../VerificationModal/VerificationModal';
import { ServiceOrIssueStatus, TrackerDataType } from '../../shared/constants/constants';
import { getQueryAdderUiPalette, queryAdderTypeScale } from '../../shared/constants/queryAdderPalette';
import { analyticsPalette, appPalette, logPalette, neutralPalette } from '../../theme/palette';
import { LOG_ADDER_CONFIG, TOPIC_LINKING_COPY } from './logAdderConfig';

const ui = {
  ...getQueryAdderUiPalette(TrackerDataType.CONTENT_LOG),
  white: neutralPalette.white,
  title: neutralPalette.black,
  text: neutralPalette.textPrimary,
  muted: neutralPalette.textSecondary,
  helper: neutralPalette.textHelper,
  headerDescription: neutralPalette.textHelper,
  cardBorder: analyticsPalette.card.borderSoft,
  divider: neutralPalette.borderSubtle,
  sectionShadow: analyticsPalette.card.shadow,
  trackingBorder: logPalette.content.border,
  trackingSurface: neutralPalette.white,
  trackingShadow: neutralPalette.shadowSoft,
  softSelectedSurface: alpha(logPalette.content.light, 0.08),
  strongSelectedSurface: alpha(logPalette.content.main, 0.08),
  selectedRingSoft: alpha(logPalette.content.light, 0.18),
  selectedRingStrong: alpha(logPalette.content.main, 0.18),
  interactiveBorder: alpha(logPalette.content.main, 0.18),
  iconBorder: alpha(logPalette.content.main, 0.12),
  service: logPalette.service.main,
  serviceDark: logPalette.service.dark,
  serviceTile: logPalette.service.surface,
};

const contentAdderConfig = LOG_ADDER_CONFIG.content;
const inheritedContextMessage =
  'These details come from the linked Service. Open the Service to edit shared project context.';

type QueryAdderFields = Record<string, any>;
type DropdownTable = Record<string, any>;
type QueryAdderCallback = (value: any, field: string) => void;
type QueryAdderDragHandler = (event: DragEvent<any>) => void;
type InheritedChipEntry = { name: string; chipColor?: string | null };

interface ContentLogQueryAdderLayoutProps {
  title: string;
  fields: QueryAdderFields;
  dropdownTable: DropdownTable;
  requiredInputs: string[];
  errorCols: string[];
  eventHandler: Record<string, any>;
  files?: any[];
  handleDrag: QueryAdderDragHandler;
  handleDrop: QueryAdderDragHandler;
  isDragActive?: boolean;
  isUpdatingRow?: boolean;
  currMonth?: string | number | Date;
  callback: QueryAdderCallback;
  onSubmit: () => void;
  onCancel: () => void;
  loading?: boolean;
  isUpdatingItem?: boolean;
  onDelete?: () => void;
  parentServiceContext?: {
    id: string;
    topic: string;
    types: string[];
    campaigns: string[];
    units: string[];
    departments: string[];
    leadExperts: string[];
  } | null;
}

interface RenderCellOptions {
  fullWidth?: boolean;
  placeholderText?: string;
  label?: string;
  info?: string;
}

interface InheritedFieldsProps {
  selectedService: any;
  typeChipOptions: InheritedChipEntry[];
  campaignChipOptions: InheritedChipEntry[];
  unitChipOptions: InheritedChipEntry[];
  departmentChipOptions: InheritedChipEntry[];
  leadExpertChipOptions: InheritedChipEntry[];
  renderInheritedChips: (entries: InheritedChipEntry[], options?: { withCampaignStripe?: boolean }) => JSX.Element;
  hasInheritedValues: (entries: InheritedChipEntry[]) => boolean;
}

const getDeleteRequestHandler = (
  onDelete: ContentLogQueryAdderLayoutProps['onDelete'],
  setOpenVerificationModal: Dispatch<SetStateAction<boolean>>
) => {
  if (!onDelete) return undefined;
  return () => setOpenVerificationModal(true);
};

function ContentLogDeleteVerificationModal({
  openVerificationModal,
  setOpenVerificationModal,
  onDelete,
}: Readonly<{
  openVerificationModal: boolean;
  setOpenVerificationModal: Dispatch<SetStateAction<boolean>>;
  onDelete?: () => void;
}>) {
  if (!onDelete) return null;

  return (
    <VerificationModal
      openVerificationModal={openVerificationModal}
      setOpenVerificationModal={setOpenVerificationModal}
      modalTitle={'Confirm Deletion'}
      modalDescription={'Are you sure you want to delete this Content Log?'}
      accentColor={ui.main}
      handleYesClick={() => {
        onDelete();
        setOpenVerificationModal(false);
      }}
    />
  );
}

function InheritedFieldItem({
  show,
  label,
  children,
}: Readonly<{ show: boolean; label: string; children: ReactNode }>) {
  if (!show) {
    return null;
  }

  return (
    <QueryAdderFieldGridItem>
      <FieldLabel label={label} ui={ui} showInfoIcon={false} />
      {children}
    </QueryAdderFieldGridItem>
  );
}

function ContentLogInheritedFields({
  selectedService,
  typeChipOptions,
  campaignChipOptions,
  unitChipOptions,
  departmentChipOptions,
  leadExpertChipOptions,
  renderInheritedChips,
  hasInheritedValues,
}: Readonly<InheritedFieldsProps>) {
  if (!selectedService) {
    return null;
  }

  return (
    <QueryAdderInheritedParentServiceSection ui={ui} infoText={inheritedContextMessage}>
      <QueryAdderFieldGridItem fullWidth>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <LockOutlinedIcon sx={{ fontSize: 13, color: ui.helper }} />
          <Typography sx={{ fontSize: queryAdderTypeScale.helperText, color: ui.helper, lineHeight: 1.45 }}>
            {inheritedContextMessage}
          </Typography>
        </Stack>
      </QueryAdderFieldGridItem>
      <InheritedFieldItem show={hasInheritedValues(typeChipOptions)} label="Type(s) of Work">
        {renderInheritedChips(typeChipOptions)}
      </InheritedFieldItem>
      <InheritedFieldItem show={hasInheritedValues(campaignChipOptions)} label="Campaign">
        {renderInheritedChips(campaignChipOptions, { withCampaignStripe: true })}
      </InheritedFieldItem>
      <InheritedFieldItem show={hasInheritedValues(unitChipOptions)} label="Unit">
        {renderInheritedChips(unitChipOptions)}
      </InheritedFieldItem>
      <InheritedFieldItem show={hasInheritedValues(departmentChipOptions)} label="Department">
        {renderInheritedChips(departmentChipOptions)}
      </InheritedFieldItem>
      <InheritedFieldItem show={hasInheritedValues(leadExpertChipOptions)} label="Lead/Expert">
        {renderInheritedChips(leadExpertChipOptions)}
      </InheritedFieldItem>
    </QueryAdderInheritedParentServiceSection>
  );
}

function ContentLogQueryAdderLayout(props: Readonly<ContentLogQueryAdderLayoutProps>) {
  const {
    fields,
    dropdownTable,
    requiredInputs,
    errorCols,
    eventHandler,
    files,
    handleDrag,
    handleDrop,
    isDragActive,
    isUpdatingRow,
    currMonth,
    callback,
    onSubmit,
    onCancel,
    loading,
    isUpdatingItem,
    onDelete,
    parentServiceContext,
  } = props;

  const [isLinkedToService, setIsLinkedToService] = useState(
    Array.isArray(fields.linkedService) && fields.linkedService.length > 0
  );
  const [isTopicLinkingExpanded, setIsTopicLinkingExpanded] = useState(true);
  const [focusLinkedService, setFocusLinkedService] = useState(false);
  const [openVerificationModal, setOpenVerificationModal] = useState(false);
  const handleDeleteRequest = getDeleteRequestHandler(onDelete, setOpenVerificationModal);
  useEffect(() => {
    if (Array.isArray(fields.linkedService) && fields.linkedService.length > 0 && !isLinkedToService) {
      setIsLinkedToService(true);
    }
  }, [fields.linkedService, isLinkedToService]);

  const selectedService = useMemo<any>(() => {
    const linkedService = Array.isArray(fields.linkedService) ? fields.linkedService[0] : null;
    if (!linkedService) return null;
    const linkedServiceId = linkedService._id || linkedService.id || linkedService;
    return (dropdownTable?.service || []).find((service: any) => service._id === linkedServiceId) || linkedService;
  }, [dropdownTable, fields.linkedService]);

  const selectedServiceId = selectedService?._id || selectedService?.id || '';
  const selectedServiceTitle = selectedService?.name || selectedService?.service || '';

  useEffect(() => {
    if (!isLinkedToService || !selectedServiceTitle) return;
    callback(selectedServiceTitle, 'topic');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinkedToService, selectedServiceTitle]);

  const handleLinkedServiceToggle = (nextChecked: boolean) => {
    setIsLinkedToService(nextChecked);
    if (nextChecked && !selectedServiceTitle) {
      setFocusLinkedService(true);
      setIsTopicLinkingExpanded(true);
    }
    if (!nextChecked) {
      callback([], 'linkedService');
      setIsTopicLinkingExpanded(true);
    }
  };
  useEffect(() => {
    if (!focusLinkedService) return;
    const timer = setTimeout(() => setFocusLinkedService(false), 300);
    return () => clearTimeout(timer);
  }, [focusLinkedService]);

  const fieldLabels = {
    topic: 'Content Title',
    contentType: 'Content Type',
    type: 'Optional Types of Work',
    expert: 'Lead/Expert',
    campaign: 'Campaign',
    lead: 'Team Members',
    audience: 'Audience',
    units: 'Unit',
    dept: 'Department',
  };

  const infoText = {
    contentType: 'Choose the format or deliverable this Content Log item represents.',
    type: 'Optionally add Types of Work tags for categorization and reporting.',
    audience:
      'Audience helps teams organize work, target messages more clearly, and interpret impact more easily over time.',
    topic:
      'Give this specific content item a clear name. The linked Service keeps it connected to the broader project.',
    expert:
      'Use Lead/Expert for the requester, spokesperson, subject-matter expert, or internal owner most closely tied to this Content.',
    campaign: 'Use Campaign to keep related multi-part work grouped under the same initiative when relevant.',
    lead: 'Use Team Members for the people doing the work. This improves ownership tracking and makes team analytics more accurate.',
  };
  const unitInfoText = 'Select the units connected to this Content entry.';
  const deptInfoText = 'Add the department when it helps clarify ownership or the team context for this Content.';
  const entryDateInfoText = 'Use Entry Date to mark when the task or work begins.';
  const dueDateInfoText = 'Use Due Date for the target timeline, or as the completion date once the work is finished.';
  const statusInfoText = 'Set the current stage of this Content record.';

  const renderLabeledCell = (col: string, { fullWidth = false, placeholderText }: RenderCellOptions = {}) => (
    <QueryAdderFieldGridItem key={col} fullWidth={fullWidth}>
      <FieldLabel
        label={(fieldLabels as Record<string, string>)[col] || col}
        required={requiredInputs.includes(col)}
        info={(infoText as Record<string, string>)[col]}
        ui={ui}
      />
      {renderQueryAdderCell({
        col,
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
        hideLabel: true,
        labelText: (fieldLabels as Record<string, string>)[col] || col,
        fullWidthContainer: true,
        placeholderText,
      })}
    </QueryAdderFieldGridItem>
  );

  const renderGridCell = (col: string, { fullWidth = false, label, info }: RenderCellOptions = {}) => (
    <QueryAdderFieldGridItem key={col} fullWidth={fullWidth}>
      {label ? <FieldLabel label={label} required={requiredInputs.includes(col)} info={info} ui={ui} /> : null}
      {renderQueryAdderCell({
        col,
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
        hideLabel: Boolean(label),
        labelText: label,
        fullWidthContainer: true,
      })}
    </QueryAdderFieldGridItem>
  );

  const topicLinkingSearchField = (
    <Box sx={{ width: '100%' }}>
      {renderQueryAdderCell({
        col: 'linkedService',
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
        hideLabel: true,
        labelText: 'Search Services',
        placeholderText: 'Search Services',
        autoFocusLinkedService: focusLinkedService,
        fullWidthContainer: true,
      })}
    </Box>
  );

  const normalizeLabel = (value: string) => value.trim().toLowerCase();

  const resolveColorToken = (rawColor: unknown, { allowHex = false } = {}): string | null => {
    if (typeof rawColor !== 'string') {
      return null;
    }
    const normalizedColor = rawColor.trim();
    if (normalizedColor === 'Green') {
      return appPalette.type.proactive;
    }
    if (normalizedColor === 'Red') {
      return appPalette.type.reactive;
    }
    if (!normalizedColor || normalizedColor.toLowerCase() === 'none') {
      return null;
    }
    return allowHex ? normalizedColor : null;
  };

  const resolveInheritedChipOptions = (
    values: string[] | undefined,
    {
      dropdownOptions,
      linkedServiceOptions,
      allowHex = false,
    }: {
      dropdownOptions: Array<{ name?: string; backgroundColor?: string; color?: string }>;
      linkedServiceOptions: Array<{ name?: string; backgroundColor?: string; color?: string }>;
      allowHex?: boolean;
    }
  ) => {
    if (!Array.isArray(values) || values.length === 0) {
      return [];
    }

    const mergedOptions = [...linkedServiceOptions, ...dropdownOptions];

    return values.map((value) => {
      const matched = mergedOptions.find(
        (option) => option?.name && normalizeLabel(option.name) === normalizeLabel(value)
      );
      return {
        name: value,
        chipColor: resolveColorToken(matched?.backgroundColor ?? matched?.color, { allowHex }),
      };
    });
  };

  const typeChipOptions = resolveInheritedChipOptions(parentServiceContext?.types, {
    dropdownOptions: Array.isArray(dropdownTable?.type) ? dropdownTable.type : [],
    linkedServiceOptions: Array.isArray(selectedService?.type) ? selectedService.type : [],
  });

  const campaignChipOptions = resolveInheritedChipOptions(parentServiceContext?.campaigns, {
    dropdownOptions: Array.isArray(dropdownTable?.campaigns) ? dropdownTable.campaigns : [],
    linkedServiceOptions: Array.isArray(selectedService?.campaign) ? selectedService.campaign : [],
    allowHex: true,
  });

  const inheritedChipRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap',
    minHeight: 28,
  };

  const getInheritedChipSx = ({
    chipColor,
    withCampaignStripe = false,
  }: {
    chipColor?: string | null;
    withCampaignStripe?: boolean;
  }) => {
    if (!chipColor) {
      return {
        borderRadius: '999px',
        minHeight: 28,
        height: 'auto',
        backgroundColor: ui.tagSurface,
        border: `1px solid ${ui.tagBorder}`,
      };
    }

    if (withCampaignStripe) {
      return {
        borderRadius: '999px',
        minHeight: 28,
        height: 'auto',
        backgroundColor: ui.tagSurface,
        border: `1px solid ${ui.tagBorder}`,
        borderLeft: `4px solid ${chipColor}`,
      };
    }

    return {
      borderRadius: '999px',
      minHeight: 28,
      height: 'auto',
      backgroundColor: alpha(chipColor, 0.44),
      border: `1px solid ${alpha(chipColor, 0.45)}`,
    };
  };

  const renderInheritedChips = (
    entries: InheritedChipEntry[],
    { withCampaignStripe = false }: { withCampaignStripe?: boolean } = {}
  ) => {
    if (entries.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: ui.helper, fontSize: queryAdderTypeScale.helperText }}>
          Not set
        </Typography>
      );
    }

    return (
      <Box sx={inheritedChipRowSx}>
        {entries.map((entry, idx) => (
          <Chip
            key={`${entry.name}-${idx}`}
            label={entry.name}
            sx={getInheritedChipSx({ chipColor: entry.chipColor, withCampaignStripe })}
          />
        ))}
      </Box>
    );
  };

  const hasInheritedValues = (entries: Array<{ name: string; chipColor?: string | null }>) =>
    Array.isArray(entries) && entries.length > 0;

  const unitChipOptions = (parentServiceContext?.units ?? []).map((name) => ({
    name,
    chipColor: null,
  }));

  const departmentChipOptions = (parentServiceContext?.departments ?? []).map((name) => ({
    name,
    chipColor: null,
  }));

  const leadExpertChipOptions = (parentServiceContext?.leadExperts ?? []).map((name) => ({
    name,
    chipColor: null,
  }));
  const inheritedFields = (
    <ContentLogInheritedFields
      selectedService={selectedService}
      typeChipOptions={typeChipOptions}
      campaignChipOptions={campaignChipOptions}
      unitChipOptions={unitChipOptions}
      departmentChipOptions={departmentChipOptions}
      leadExpertChipOptions={leadExpertChipOptions}
      renderInheritedChips={renderInheritedChips}
      hasInheritedValues={hasInheritedValues}
    />
  );

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        color: ui.text,
        backgroundColor: ui.white,
        fontFamily: ui.bodyFontFamily,
      }}
    >
      <QueryAdderScopedStyles
        scopeClassName=".content-query-adder"
        ui={ui}
        options={{ preserveFieldChrome: true, preserveRichTextChrome: true }}
      />
      <Box
        className="content-query-adder"
        sx={{ height: '100%', minHeight: 0, color: ui.text, backgroundColor: ui.white }}
      >
        <QueryAdderModalShell
          header={
            <QueryAdderStickyHeader
              title={contentAdderConfig.modalTitle}
              infoText="Use Content Log for the individual deliverables or supporting tasks tied to a related Service so work stays connected in one workflow."
              icon={contentAdderConfig.icon}
              ui={ui}
              onClose={onCancel}
              onSubmit={onSubmit}
              onCancel={onCancel}
              onDelete={handleDeleteRequest}
              isUpdatingItem={isUpdatingItem || loading}
              isUpdatingRow={isUpdatingRow}
              topBorderColor={contentAdderConfig.logColor}
              titleSx={{ color: ui.title }}
              iconCardSx={{
                backgroundColor: ui.tile,
                color: ui.main,
                borderRadius: '12px',
              }}
            />
          }
          bodySx={{ backgroundColor: ui.white }}
          contentSx={{ pb: 4, backgroundColor: ui.white }}
        >
          {loading ? (
            <QueryAdderLayoutSkeleton ui={ui} />
          ) : (
            <Box component="form" onSubmit={onSubmit} sx={{ backgroundColor: ui.white }}>
              <Stack spacing={3}>
                <QueryAdderTopicLinkingCard
                  ui={ui}
                  eyebrow={TOPIC_LINKING_COPY.sectionTitle}
                  checkboxLabel={TOPIC_LINKING_COPY.panelTitle}
                  checkboxHelperText={TOPIC_LINKING_COPY.helperText}
                  checked={isLinkedToService}
                  onCheckedChange={handleLinkedServiceToggle}
                  selectedServiceTitle={selectedServiceTitle}
                  selectedServiceId={selectedServiceId}
                  expanded={isTopicLinkingExpanded}
                  onExpandedChange={setIsTopicLinkingExpanded}
                  searchField={topicLinkingSearchField}
                  inheritedFields={inheritedFields}
                  inheritedInfoText="Use this when this record belongs under an existing Service."
                />

                <SectionCard title="Details" icon={<AssignmentOutlinedIcon sx={{ fontSize: 20 }} />} ui={ui}>
                  <Box sx={queryAdderTwoColumnGridSx}>
                    {renderLabeledCell('topic', { fullWidth: true })}
                    {isLinkedToService ? null : (
                      <>
                        {renderGridCell('units', { label: 'Unit', info: unitInfoText })}
                        {renderGridCell('dept', { label: 'Department', info: deptInfoText })}
                        {renderLabeledCell('expert')}
                        {renderLabeledCell('campaign')}
                      </>
                    )}
                    {renderLabeledCell('contentType')}
                    {renderLabeledCell('type')}
                    {renderLabeledCell('audience')}
                    {renderGridCell('entryDate', { label: 'Entry Date', info: entryDateInfoText })}
                    {renderGridCell('dueDate', { label: 'Due Date', info: dueDateInfoText })}
                    {renderLabeledCell('lead')}
                    <QueryAdderFieldGridItem>
                      <FieldLabel
                        label="Status"
                        required={requiredInputs.includes('status')}
                        info={statusInfoText}
                        ui={ui}
                      />
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {[
                          ServiceOrIssueStatus.IN_PROGRESS,
                          ServiceOrIssueStatus.COMPLETED,
                          ServiceOrIssueStatus.PARTICIPATION_RIBBON,
                          ServiceOrIssueStatus.CANCELLED,
                        ].map((statusOption) => (
                          <StatusPill
                            key={statusOption}
                            label={statusOption as any}
                            active={fields.status === statusOption}
                            onClick={() => callback(statusOption, 'status')}
                            ui={ui}
                          />
                        ))}
                      </Stack>
                    </QueryAdderFieldGridItem>
                  </Box>
                </SectionCard>

                <SectionCard title="Notes and Links" icon={<FolderOutlinedIcon sx={{ fontSize: 20 }} />} ui={ui}>
                  <Box sx={queryAdderOneColumnFieldGridSx}>
                    {renderGridCell('otherNotes', { label: 'Other Notes', fullWidth: true })}
                    {renderGridCell('content', { label: 'Content', fullWidth: true })}
                    {renderGridCell('background', { label: 'Background Information', fullWidth: true })}
                  </Box>

                  <Box sx={{ ...queryAdderTwoColumnGridSx, mt: 2.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel
                        label="Files and Links"
                        info="Store working files and related links during development so the full working history stays in the item."
                        ui={ui}
                      />
                      <QueryAdderUploadDropzone
                        ui={ui}
                        description="Store working files and related links during development so the full working history stays in the item."
                        showIcon={false}
                        handleDrag={handleDrag}
                        handleDrop={handleDrop}
                        isDragActive={isDragActive}
                      >
                        {renderQueryAdderCell({
                          col: 'file',
                          fields,
                          dropdownTable,
                          callback,
                          currMonth,
                          requiredInputs,
                          errorCols,
                          eventHandler,
                          files,
                          handleDrag,
                          handleDrop,
                          isDragActive,
                          isUpdatingRow,
                          hideLabel: true,
                          fullWidthContainer: true,
                          fileRenderMode: 'uploadOnly',
                        })}
                      </QueryAdderUploadDropzone>
                      {files && files.length > 0 ? (
                        <Box sx={{ mt: 2 }}>
                          {renderQueryAdderCell({
                            col: 'file',
                            fields,
                            dropdownTable,
                            callback,
                            currMonth,
                            requiredInputs,
                            errorCols,
                            eventHandler,
                            files,
                            handleDrag,
                            handleDrop,
                            isDragActive,
                            isUpdatingRow,
                            hideLabel: true,
                            fullWidthContainer: true,
                            fileRenderMode: 'listOnly',
                          })}
                        </Box>
                      ) : null}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel
                        label="Live Link"
                        ui={ui}
                        info="When the piece is live, add the live link so the final published content stays connected to the record."
                      />
                      <Box sx={{ mt: 1.25, width: '100%' }}>
                        {fields?.liveLink ? (
                          <Box sx={{ width: '100%' }}>
                            {renderQueryAdderCell({
                              col: 'liveLink',
                              fields,
                              dropdownTable,
                              callback,
                              currMonth,
                              requiredInputs,
                              errorCols,
                              eventHandler,
                              files,
                              handleDrag,
                              handleDrop,
                              isDragActive,
                              isUpdatingRow,
                              hideLabel: true,
                              fullWidthContainer: true,
                              liveLinkRenderMode: 'listOnly',
                            })}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              minWidth: 0,
                              border: `1px solid ${ui.divider}`,
                              borderRadius: '14px',
                              backgroundColor: ui.white,
                              px: 2.25,
                              py: 2.1,
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              {renderQueryAdderCell({
                                col: 'liveLink',
                                fields,
                                dropdownTable,
                                callback,
                                currMonth,
                                requiredInputs,
                                errorCols,
                                eventHandler,
                                files,
                                handleDrag,
                                handleDrop,
                                isDragActive,
                                isUpdatingRow,
                                hideLabel: true,
                                fullWidthContainer: true,
                                liveLinkRenderMode: 'actionOnly',
                              })}
                            </Box>
                            <Typography
                              sx={{
                                mt: 1.25,
                                fontSize: queryAdderTypeScale.helperText,
                                lineHeight: 1.5,
                                color: ui.helper,
                                textAlign: 'center',
                              }}
                            >
                              Add the published story link so this content item stays connected to the final piece.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </SectionCard>
              </Stack>
            </Box>
          )}
        </QueryAdderModalShell>
      </Box>

      <ContentLogDeleteVerificationModal
        openVerificationModal={openVerificationModal}
        setOpenVerificationModal={setOpenVerificationModal}
        onDelete={onDelete}
      />
    </Box>
  );
}

export default ContentLogQueryAdderLayout;
